/*
 * Interpolation for interface strings.
 *
 * `it.ts` had no need of it while no string carried a value inside it; `pagePosition`
 * ("Pagina {page} di {pageCount}") is the first that does.
 *
 * It does not reuse the two `interpolate` helpers in `errors.ts` and `field-errors.ts`: both
 * are **private** to their modules and take `ProblemParams`, the params of a problem
 * document, which admit `null` and nested structures. Here the values are the ones a
 * component has in hand. Unifying the three would mean one signature serving two different
 * domains, and it does not pay: the triplication is deliberate and recorded here.
 */

/** The values a component can interpolate: no `null`, no nesting. */
export type InterpolationValues = Readonly<Record<string, string | number>>

/**
 * Replaces `{key}` with the matching value.
 *
 * A placeholder with no value **stays in the text** instead of becoming `undefined`: it
 * shows up in QA and does not produce a mutilated sentence in production. It is the same
 * policy the two catalogues follow, and the one thing worth keeping identical across all
 * three.
 */
export function interpolate(text: string, values: InterpolationValues): string {
  return text.replace(/\{(\w+)\}/g, (placeholder, key: string) => {
    // hasOwn rather than a direct lookup: `{toString}` would resolve to a function on the
    // prototype, and the sentence would show source code to a user.
    if (!Object.hasOwn(values, key)) return placeholder
    return String(values[key])
  })
}

/** A count with its noun, singular for one: «1 partita», «8 partite». The two forms are two
 *  catalogue keys, since Italian and English do not agree on more than that. */
export function countPhrase(count: number, one: string, many: string): string {
  return count === 1 ? one : interpolate(many, { count })
}
